import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, ChatCircleText, Warning, Robot, Headset } from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";

const SLA_MINUTES = 15;

export function isOverdue(contact, nowMs = Date.now()) {
  if (!contact) return false;
  if (contact.status === "resolved") return false;
  if (contact.last_direction !== "inbound") return false;
  const ts = contact.last_timestamp;
  if (!ts) return false;
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return false;
  return nowMs - t > SLA_MINUTES * 60 * 1000;
}

function initials(name, phone) {
  const src = (name || phone || "?").trim();
  return src.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

const STATUS_DOT = {
  new: "bg-[#F59E0B]",
  in_progress: "bg-[#3B82F6]",
  resolved: "bg-[#10B981]",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "agent", label: "Agent" },
  { key: "overdue", label: "Overdue" },
  { key: "mine", label: "Mine" },
  { key: "new", label: "New" },
  { key: "in_progress", label: "Active" },
  { key: "resolved", label: "Done" },
];

export default function Sidebar({
  contacts,
  counts,
  selected,
  onSelect,
  search,
  setSearch,
  filter,
  setFilter,
  currentUserId,
  mobileHidden = false,
}) {
  // Re-render every 30s so "overdue" state updates as time passes
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  return (
    <aside
      className={`w-full md:w-[340px] md:shrink-0 h-full border-r border-[var(--border)] bg-[var(--surface)] flex-col ${
        mobileHidden ? "hidden md:flex" : "flex"
      }`}
      data-testid="sidebar"
    >
      <div className="px-4 py-3 border-b border-[var(--border)] space-y-3">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[var(--muted)]">Inbox</div>

        <div className="relative">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search driver…"
            className="pl-9 h-9 bg-[var(--bg)] border-[var(--border)]"
            data-testid="sidebar-search-input"
          />
        </div>
        <div className="flex items-center gap-1 text-xs flex-wrap">
          {FILTERS.map((f) => {
            const count = counts?.[f.key] ?? 0;
            const isOverdueTab = f.key === "overdue" || f.key === "agent";
            const active = filter === f.key;
            const alert = isOverdueTab && count > 0 && !active;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  active
                    ? isOverdueTab && count > 0
                      ? "bg-[#EF4444] text-white"
                      : "bg-[var(--fg)] text-[var(--bg)]"
                    : alert
                    ? "bg-[#FEE2E2] text-[#B91C1C] font-semibold hover:bg-[#FECACA]"
                    : "hover:bg-[var(--bg)] text-[var(--muted)]"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}{" "}
                <span className="opacity-70 ml-0.5">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <ScrollArea className="flex-1 scroll-thin">
        {contacts.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--muted)] flex flex-col items-center gap-2">
            <ChatCircleText size={28} className="text-[var(--border)]" />
            No conversations.
          </div>
        ) : (
          <ul className="py-1">
            {contacts.map((c) => {
              const active = c.phone === selected;
              const mine =
                !!currentUserId &&
                c.assigned_to?.id === currentUserId &&
                c.status !== "resolved";
              const overdue = isOverdue(c, nowTick);
              return (
                <li key={c.phone}>
                  <button
                    type="button"
                    onClick={() => onSelect(c.phone)}
                    data-testid={`conversation-item-${c.phone}`}
                    className={`w-full text-left px-3.5 py-3.5 flex gap-3 items-start transition-colors border-l-[3px] ${
                      active
                        ? "bg-[var(--bg)] border-l-[var(--fg)]"
                        : overdue
                        ? "bg-[#FEE2E2]/60 dark:bg-[#7F1D1D]/25 border-l-[#EF4444] hover:bg-[#FEE2E2]/80 dark:hover:bg-[#7F1D1D]/35"
                        : mine
                        ? "bg-[#FEF3C7]/40 dark:bg-[#78350F]/20 border-l-[#F59E0B] hover:bg-[#FEF3C7]/60 dark:hover:bg-[#78350F]/30"
                        : "border-l-transparent hover:bg-[var(--bg)]"
                    }`}
                  >
                    <div className="relative">
                      <Avatar className="h-11 w-11">
                        <AvatarFallback className="bg-[var(--fg)] text-[var(--bg)] text-xs">
                          {initials(c.name, c.phone)}
                        </AvatarFallback>
                      </Avatar>
                      <span
                        className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[var(--surface)] ${
                          STATUS_DOT[c.status] || STATUS_DOT.new
                        }`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm flex items-center gap-1.5 min-w-0">
                          <span className="truncate">{c.name || c.phone}</span>
                          {overdue && (
                            <span
                              className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#EF4444] text-white flex items-center gap-0.5"
                              data-testid="overdue-badge"
                              title={`Customer waiting > ${SLA_MINUTES} min`}
                            >
                              <Warning size={10} weight="fill" /> Overdue
                            </span>
                          )}
                          {c.needs_agent && (
                            <span
                              className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#DC2626] text-white flex items-center gap-0.5 animate-pulse shrink-0"
                              data-testid={`agent-request-badge-${c.phone}`}
                              title="Driver wants to talk to an agent"
                            >
                              <Headset size={10} weight="fill" /> Agent
                            </span>
                          )}
                          {mine && !overdue && (
                            <span
                              className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#F59E0B] text-white"
                              data-testid="mine-badge"
                              title="Assigned to you"
                            >
                              Mine
                            </span>
                          )}
                        </div>
                        {c.last_timestamp && (
                          <div className={`text-[10px] shrink-0 ${overdue ? "text-[#B91C1C] font-semibold" : "text-[var(--muted)]"}`}>
                            {formatDistanceToNow(new Date(c.last_timestamp), { addSuffix: false })}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-[var(--muted)] mt-1 flex items-start gap-1.5">
                        <span className="flex-1 min-w-0 line-clamp-2 leading-snug">
                          {c.last_message || "…"}
                        </span>
                        {c.unread > 0 && (
                          <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-white h-4 px-1.5 text-[10px] shrink-0 mt-0.5">
                            {c.unread}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <div className="text-[10px] text-[var(--muted)] font-mono">+{c.phone}</div>
                        {c.assigned_to?.id === "bot" ? (
                          <span className="text-[10px] px-1.5 rounded bg-[#D1FAE5] text-[#065F46] flex items-center gap-0.5" data-testid={`assignee-${c.phone}`}>
                            <Robot size={10} weight="fill" /> Bot
                          </span>
                        ) : c.assigned_to && !mine && (
                          <span className="text-[10px] px-1.5 rounded bg-[var(--bg)] text-[var(--muted)]" data-testid={`assignee-${c.phone}`}>
                            @{(c.assigned_to.full_name || c.assigned_to.email || "").split("@")[0]}
                          </span>
                        )}
                        {c.bot_paused && (
                          <span className="text-[10px] px-1.5 rounded bg-[#FEF3C7] text-[#92400E]" title="Bot paused for this chat" data-testid={`bot-off-${c.phone}`}>
                            Bot off
                          </span>
                        )}
                        {mine && overdue && (
                          <span className="text-[10px] px-1.5 rounded bg-[#F59E0B] text-white font-medium">
                            You
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>
    </aside>
  );
}
